import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useToast } from '../components/toast';
import { api, ApiError, call } from './client';
import { useHousehold } from './session';

/**
 * Every household query is keyed under ['f', familyId, …] so a single invalidation refreshes the whole household
 * when the server's change counter moves (docs/notifications.md §4).
 */
export const keys = {
  family: (fid: string) => ['f', fid] as const,
  sync: (fid: string) => ['sync', fid] as const,
};

const path = (familyId: string) => ({ path: { familyId } });

export function useSyncPolling() {
  const { familyId } = useHousehold();
  const qc = useQueryClient();
  const last = useRef<number | null>(null);
  const q = useQuery({
    queryKey: keys.sync(familyId),
    queryFn: () => call(api.GET('/api/v1/families/{familyId}/sync-state', { params: path(familyId) })),
    refetchInterval: 30_000, // paused automatically while the tab is hidden; refetches on focus
  });
  useEffect(() => {
    const seq = q.data?.changeSeq;
    if (seq === undefined) return;
    if (last.current !== null && last.current !== seq) void qc.invalidateQueries({ queryKey: keys.family(familyId) });
    last.current = seq;
  }, [q.data?.changeSeq, familyId, qc]);
  return { unreadNotifications: q.data?.unreadNotificationCount ?? 0 };
}

/**
 * Runs a command; afterwards refreshes household data. Conflicts (409) are expected in a shared household:
 * show the server's explanation and refresh — never retry silently (docs/concurrency.md §6).
 */
export function useAction<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>, opts: { success?: string } = {}) {
  const { familyId } = useHousehold();
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      if (opts.success) toast.show(opts.success);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) toast.show(err.userMessage, 'warning');
      else if (err instanceof ApiError && err.status !== 400) toast.show(err.userMessage, 'error');
      else if (!(err instanceof ApiError)) toast.show("Couldn't reach FamilyChores. Check your connection.", 'error');
    },
    onSettled: async () => {
      await Promise.all([qc.invalidateQueries({ queryKey: keys.family(familyId) }), qc.invalidateQueries({ queryKey: keys.sync(familyId) })]);
    },
  });
}

function useFamilyQuery<T>(sub: readonly unknown[], fn: (familyId: string) => Promise<T>, enabled = true) {
  const { familyId } = useHousehold();
  return useQuery({ queryKey: [...keys.family(familyId), ...sub], queryFn: () => fn(familyId), enabled, placeholderData: keepPreviousData });
}

export const useFamily = () => useFamilyQuery(['family'], (fid) => call(api.GET('/api/v1/families/{familyId}', { params: path(fid) })));
export const useMembers = () => useFamilyQuery(['members'], (fid) => call(api.GET('/api/v1/families/{familyId}/members', { params: path(fid) })));
export const useDashboard = () => useFamilyQuery(['dashboard'], (fid) => call(api.GET('/api/v1/families/{familyId}/dashboard', { params: path(fid) })));
export const usePriorities = () => useFamilyQuery(['priorities'], (fid) => call(api.GET('/api/v1/families/{familyId}/priorities', { params: path(fid) })));
export const usePendingDecisions = () =>
  useFamilyQuery(['pending'], (fid) => call(api.GET('/api/v1/families/{familyId}/pending-decisions', { params: path(fid) })));
export const useInvitations = () =>
  useFamilyQuery(['invitations'], (fid) => call(api.GET('/api/v1/families/{familyId}/invitations', { params: path(fid) })));

export interface TaskFilter {
  assignee?: string;
  status?: 'open' | 'completed' | 'skipped' | 'archived' | 'all';
  sort?: 'priority' | 'due' | 'created' | 'completed';
  seriesId?: string;
  parentTaskId?: string;
}

export const useTasks = (filter: TaskFilter, enabled = true) =>
  useFamilyQuery(['tasks', filter], (fid) => call(api.GET('/api/v1/families/{familyId}/tasks', { params: { ...path(fid), query: { limit: 100, ...filter } } })), enabled);

export const useTask = (taskId: string) =>
  useFamilyQuery(['task', taskId], (fid) => call(api.GET('/api/v1/families/{familyId}/tasks/{taskId}', { params: { path: { familyId: fid, taskId } } })));
export const useTaskHistory = (taskId: string) =>
  useFamilyQuery(['task', taskId, 'history'], (fid) =>
    call(api.GET('/api/v1/families/{familyId}/tasks/{taskId}/history', { params: { path: { familyId: fid, taskId } } })),
  );
export const useComments = (taskId: string) =>
  useFamilyQuery(['task', taskId, 'comments'], (fid) =>
    call(api.GET('/api/v1/families/{familyId}/tasks/{taskId}/comments', { params: { path: { familyId: fid, taskId } } })),
  );

export const useSeriesList = (status: 'active' | 'ended' | 'all' = 'active') =>
  useFamilyQuery(['series', status], (fid) => call(api.GET('/api/v1/families/{familyId}/series', { params: { ...path(fid), query: { status } } })));
export const useSeries = (seriesId: string) =>
  useFamilyQuery(['series', 'one', seriesId], (fid) =>
    call(api.GET('/api/v1/families/{familyId}/series/{seriesId}', { params: { path: { familyId: fid, seriesId } } })),
  );
export const useSeriesHistory = (seriesId: string) =>
  useFamilyQuery(['series', 'one', seriesId, 'history'], (fid) =>
    call(api.GET('/api/v1/families/{familyId}/series/{seriesId}/history', { params: { path: { familyId: fid, seriesId } } })),
  );

export const useDelegation = (proposalId: string) =>
  useFamilyQuery(['delegation', proposalId], (fid) =>
    call(api.GET('/api/v1/families/{familyId}/child-delegations/{proposalId}', { params: { path: { familyId: fid, proposalId } } })),
  );
export const usePriorityRequest = (requestId: string) =>
  useFamilyQuery(['priority', requestId], (fid) =>
    call(api.GET('/api/v1/families/{familyId}/priority-change-requests/{requestId}', { params: { path: { familyId: fid, requestId } } })),
  );
export const useChildAssignments = (childMemberId?: string) =>
  useFamilyQuery(['assignments', childMemberId], (fid) =>
    call(api.GET('/api/v1/families/{familyId}/child-assignments', { params: { ...path(fid), query: { childMemberId } } })),
  );
export const useFamilyActivities = (scope: 'upcoming' | 'past' = 'upcoming') =>
  useFamilyQuery(['activities', scope], (fid) => call(api.GET('/api/v1/families/{familyId}/activities', { params: { ...path(fid), query: { scope } } })));
export const useFamilyActivity = (activityId: string) =>
  useFamilyQuery(['activities', 'one', activityId], (fid) =>
    call(api.GET('/api/v1/families/{familyId}/activities/{activityId}', { params: { path: { familyId: fid, activityId } } })),
  );
export const useRewards = () => useFamilyQuery(['rewards'], (fid) => call(api.GET('/api/v1/families/{familyId}/rewards', { params: path(fid) })));
export const usePoints = (childId: string) =>
  useFamilyQuery(['points', childId], (fid) => call(api.GET('/api/v1/families/{familyId}/children/{childId}/points', { params: { path: { familyId: fid, childId } } })));
export const usePointEntries = (childId: string) =>
  useFamilyQuery(['points', childId, 'entries'], (fid) =>
    call(api.GET('/api/v1/families/{familyId}/children/{childId}/points/entries', { params: { path: { familyId: fid, childId } } })),
  );
export const useNotifications = () =>
  useFamilyQuery(['notifications'], (fid) => call(api.GET('/api/v1/families/{familyId}/notifications', { params: { ...path(fid), query: {} } })));
export const useActivity = () => useFamilyQuery(['activity'], (fid) => call(api.GET('/api/v1/families/{familyId}/activity', { params: { ...path(fid), query: {} } })));
